import type { CandidateView, MarkVerdict, ReviewMark, ReviewUnit } from "../../../api/content/curationApi";
import type { TermLabel } from "../../../api/content/ontologyApi";
import type { ConflictLevel, Review } from "../../../api/content/reviewDecoders";
import { predicateKey } from "../candidates/candidateModel";
import { labelIn } from "../conflicts/conflictTermNames";
import { parseNTriple } from "../../../shared/rdf/nTriples";

/**
 * The review outline (curation-ui.md CU-2): one row per unit, grouped, worst first. A
 * module with 40 changed triples about 6 terms becomes 6 short rows, each with its own
 * findings and possible duplicates. Pure: the page passes in what the server sent.
 */

export type OutlineGroupKey = "conversation" | "classes" | "properties" | "individuals" | "terms" | "items" | "affected" | "checks";
export type OutlineFilter = "all" | "needs_me" | "open" | "duplicates";

export const OUTLINE_GROUPS: readonly OutlineGroupKey[] = ["conversation", "classes", "properties", "individuals", "terms", "items", "affected", "checks"];

export interface OutlineRow {
  readonly key: string;
  readonly unit: ReviewUnit;
  readonly group: OutlineGroupKey;
  readonly name: string;
  readonly level: ConflictLevel | null;
  /** Open candidate proposals for the unit's term. */
  readonly openCandidates: number;
  readonly myMark: MarkVerdict | null;
  readonly markedBy: readonly string[];
  /** I curate an item of the unit and have not marked it, or a proposal waits for me. */
  readonly needsMe: boolean;
}

export interface OutlineGroup {
  readonly key: OutlineGroupKey;
  readonly rows: readonly OutlineRow[];
}

export interface OutlineInputs {
  readonly review: Review;
  readonly units: readonly ReviewUnit[];
  readonly marks: readonly ReviewMark[];
  readonly candidates: readonly CandidateView[];
  readonly viewer: string | null;
  /** Whether the viewer may edit the change set (resolve proposals). */
  readonly viewerCanEdit: boolean;
  /** The reader's language, in which a term is named (R3-1). */
  readonly language: string;
  /** The review's term names in that language (`useReviewTermNames`): the conflict check's, then the data model's. */
  readonly termNames: Readonly<Record<string, string>>;
  /** The name of a term with neither a label nor a definition, in the reader's language. */
  readonly unnamedTerm: string;
}

/** What names a unit. */
export type UnitNameSources = Pick<OutlineInputs, "review" | "language" | "termNames" | "unnamedTerm">;

const RDF_TYPE = "<http://www.w3.org/1999/02/22-rdf-syntax-ns#type>";
const OWL = "http://www.w3.org/2002/07/owl#";
const PROPERTY_TYPES = ["ObjectProperty", "DatatypeProperty", "AnnotationProperty"].map((name) => `<${OWL}${name}>`);
const CLASS_TYPES = [`<${OWL}Class>`, "<http://www.w3.org/2000/01/rdf-schema#Class>"];
const SEVERITY: Readonly<Record<ConflictLevel, number>> = { conflict: 3, check: 2, info: 1 };

/** Classes, properties or individuals, from the unit's own `rdf:type` statements. */
function termGroup(unit: ReviewUnit): OutlineGroupKey {
  const types = [...unit.addedTriples, ...unit.removedTriples].filter((line) => line.includes(` ${RDF_TYPE} `));
  if (types.some((line) => CLASS_TYPES.some((type) => line.includes(type)))) {
    return "classes";
  }
  if (types.some((line) => PROPERTY_TYPES.some((type) => line.includes(type)))) {
    return "properties";
  }
  return types.length > 0 ? "individuals" : "terms";
}

function groupOf(unit: ReviewUnit): OutlineGroupKey {
  switch (unit.kind) {
    case "conversation":
      return "conversation";
    case "term":
      return termGroup(unit);
    case "item":
      return "items";
    case "affected":
      return "affected";
    case "check":
      return "checks";
  }
}

/** At most this many characters of a definition name a term without a label. */
const DEFINITION_NAME_LENGTH = 60;

/** The literals the unit's own triples, added or removed, give `term` through a predicate playing one of `fields` (`predicateKey`). */
function ownLiterals(unit: ReviewUnit, term: string, fields: readonly string[]): TermLabel[] {
  return [...unit.addedTriples, ...unit.removedTriples].flatMap((line): TermLabel[] => {
    const triple = parseNTriple(line);
    if (triple === null || triple.subject.kind !== "iri" || triple.subject.iri !== term || triple.object.kind !== "literal") return [];
    const field = predicateKey(triple.predicate);
    return field !== null && fields.includes(field) ? [{ text: triple.object.text, language: triple.object.language }] : [];
  });
}

/** The start of a definition, cut after a whole word. */
function definitionStart(text: string): string {
  const flat = text.replace(/\s+/gu, " ").trim();
  if (flat.length <= DEFINITION_NAME_LENGTH) return flat;
  const cut = flat.slice(0, DEFINITION_NAME_LENGTH);
  const space = cut.lastIndexOf(" ");
  return `${(space > 0 ? cut.slice(0, space) : cut).replace(/[,;:.]$/u, "")}…`;
}

/**
 * A readable name: the term's name among the review's term names (the conflict check's, then the
 * data model's at the change set's head), else a label among the unit's own triples, added or
 * removed; else the start of its definition; else `unnamedTerm`, never the end of its IRI (P-F;
 * usability round 4). An item by its name, a check by its item's or its summary.
 */
export function unitName(unit: ReviewUnit, { review, language, termNames, unnamedTerm }: UnitNameSources): string {
  if (unit.kind === "conversation") {
    return "";
  }
  if (unit.term !== null) {
    const term = unit.term;
    const label = termNames[term]
      ?? labelIn(ownLiterals(unit, term, ["label"]), language)
      ?? labelIn(ownLiterals(unit, term, ["altLabel"]), language);
    if (label !== undefined) return label;
    const definition = labelIn(ownLiterals(unit, term, ["definition", "comment"]), language);
    return definition === undefined ? unnamedTerm : definitionStart(definition);
  }
  const id = unit.key.slice(unit.key.indexOf(":") + 1);
  if (unit.kind === "check") {
    // A check of an item is named by the item's version reference.
    const itemId = id.split("@")[0] ?? id;
    const checked = review.items.find((entry) => entry.itemId === itemId);
    const result = [...(review.checks?.results ?? []), ...(review.checks?.processResults ?? [])].find((entry) => entry.checkId === id);
    return checked?.name ?? result?.summary ?? id;
  }
  const item = review.items.find((entry) => entry.itemId === id);
  const finding = review.conflictCheck?.findings.find((entry) => entry.item?.itemId === id);
  return item?.name ?? finding?.item?.name ?? id;
}

/** The worst conflict-check level about the unit; `check` for a failing check. */
export function unitLevel(unit: ReviewUnit, review: Review): ConflictLevel | null {
  if (unit.kind === "check") {
    return "check";
  }
  const id = unit.key.slice(unit.key.indexOf(":") + 1);
  let level: ConflictLevel | null = null;
  for (const finding of review.conflictCheck?.findings ?? []) {
    const about = unit.term !== null ? finding.term === unit.term : finding.item?.itemId === id;
    if (about && (level === null || SEVERITY[finding.level] > SEVERITY[level])) {
      level = finding.level;
    }
  }
  return level;
}

export function outlineRows(inputs: OutlineInputs): readonly OutlineRow[] {
  const covered = new Set(inputs.review.summary.callerCurates.map((item) => item.itemId));
  return inputs.units.map((unit) => {
    const marks = inputs.marks.filter((mark) => mark.unitKey === unit.key && mark.current);
    const mine = marks.find((mark) => mark.reviewer === inputs.viewer);
    const openCandidates = unit.term === null ? 0 : inputs.candidates.filter((view) => view.candidate.term === unit.term && view.candidate.state === "open").length;
    const curated = unit.items.some((item) => covered.has(item.itemId));
    return {
      key: unit.key,
      unit,
      group: groupOf(unit),
      name: unitName(unit, inputs),
      level: unitLevel(unit, inputs.review),
      openCandidates,
      myMark: mine?.verdict ?? null,
      markedBy: marks.map((mark) => mark.reviewer),
      needsMe: (curated && mine === undefined) || (openCandidates > 0 && inputs.viewerCanEdit),
    };
  });
}

function rank(row: OutlineRow): number {
  return (row.needsMe ? 10 : 0) + (row.level === null ? 0 : SEVERITY[row.level]);
}

function matches(row: OutlineRow, filter: OutlineFilter, text: string): boolean {
  if (text !== "" && !`${row.name} ${row.unit.term ?? ""}`.toLowerCase().includes(text.toLowerCase())) {
    return false;
  }
  switch (filter) {
    case "all":
      return true;
    case "needs_me":
      return row.needsMe;
    case "open":
      return row.markedBy.length === 0 && row.unit.kind !== "conversation";
    case "duplicates":
      return row.openCandidates > 0;
  }
}

/** Groups in order; inside one, needs me first, then the worst level, then the name. */
export function reviewOutline(inputs: OutlineInputs, filter: OutlineFilter = "all", text = ""): readonly OutlineGroup[] {
  const rows = outlineRows(inputs).filter((row) => row.group === "conversation" || matches(row, filter, text));
  return OUTLINE_GROUPS.map((key) => ({
    key,
    rows: rows.filter((row) => row.group === key).sort((left, right) => rank(right) - rank(left) || left.name.localeCompare(right.name)),
  })).filter((group) => group.rows.length > 0);
}

/** Open decisions, as v1's counter: open proposals, and units I curate without my mark. */
export function openDecisionCount(rows: readonly OutlineRow[]): number {
  return rows.reduce((count, row) => count + row.openCandidates + (row.needsMe && row.openCandidates === 0 ? 1 : 0), 0);
}

/**
 * The next unresolved unit after `current` (needs me first, then the worst level), or
 * the one before with `direction` -1; wraps round; `null` if none is unresolved.
 */
export function nextUnresolved(rows: readonly OutlineRow[], current: string | null, direction: 1 | -1 = 1): string | null {
  const open = rows
    .filter((row) => row.needsMe || row.openCandidates > 0 || (row.level !== null && row.level !== "info" && row.markedBy.length === 0))
    .sort((left, right) => rank(right) - rank(left));
  if (open.length === 0) {
    return null;
  }
  const index = current === null ? -1 : open.findIndex((row) => row.key === current);
  const next = index === -1 ? (direction === 1 ? 0 : open.length - 1) : (index + direction + open.length) % open.length;
  return open[next]?.key ?? null;
}

/** The unit after (or before) `current` in outline order. */
export function adjacentUnit(groups: readonly OutlineGroup[], current: string | null, direction: 1 | -1): string | null {
  const keys = groups.flatMap((group) => group.rows.map((row) => row.key));
  if (keys.length === 0) {
    return null;
  }
  const index = current === null ? -1 : keys.indexOf(current);
  const next = Math.min(keys.length - 1, Math.max(0, index + direction));
  return keys[index === -1 ? 0 : next] ?? null;
}
